import { useNavigate } from '@tanstack/react-router'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { cn } from '~/lib/utils'
import type {
  LogisticsSearch,
  OperationsDetailTab,
  SimulationDetailTab,
} from '~/routes/_authenticated/logistics/-search-schema'
import { LogisticsNavBar } from './logistics-nav-bar'

/**
 * Cadre des écrans Logistique (page à onglets, détail d'une tournée, fiche d'un camion) : titre,
 * barre d'onglets et de filtres, puis le contenu. `onSearchChange` décide de la navigation (même
 * page, ou retour à la page Logistique depuis un détail).
 */
export function LogisticsLayout({
  search,
  onSearchChange,
  mockupBackground = false,
  containerClassName,
  children,
}: React.PropsWithChildren<{
  search: LogisticsSearch
  onSearchChange: (patch: Partial<LogisticsSearch>) => void
  /** Fond beige des écrans aux maquettes (cartes claires posées dessus), contenu défilant. */
  mockupBackground?: boolean
  containerClassName?: string
}>) {
  return (
    <>
      <AppHeader>
        <Title>Logistique</Title>
      </AppHeader>
      <LogisticsNavBar search={search} onSearchChange={onSearchChange} />
      <AppContainer
        // Le fond des maquettes l'emporte sur `containerClassName` (défilement compris).
        className={cn(containerClassName, mockupBackground && 'overflow-auto bg-background-100')}
      >
        {children}
      </AppContainer>
    </>
  )
}

/**
 * Onglet du panneau d'un détail (simulation, tournée en vigueur), porté par `detailTab` : l'onglet
 * par défaut n'y figure pas. Remplacé dans l'historique : le retour du navigateur ne repasse pas par
 * chaque onglet.
 */
export const useOpenDetailTab = <TTab extends OperationsDetailTab | SimulationDetailTab>(
  defaultTab: TTab,
) => {
  const navigate = useNavigate()
  return (tab: TTab) =>
    void navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        detailTab: tab === defaultTab ? undefined : tab,
      }),
      replace: true,
    })
}

/**
 * Navigation d'un détail (tournée, camion) vers la page Logistique : un onglet ou un filtre choisi
 * dans sa barre s'y applique, les autres paramètres de `search` suivent.
 */
export const useOpenLogisticsList = (search: LogisticsSearch) => {
  const navigate = useNavigate()
  return (patch: Partial<LogisticsSearch>) =>
    void navigate({ to: '/logistics', search: { ...search, ...patch } })
}
